/**
 * HomeV3 — Editorial / Minimalist Theme
 * Clean white/dark backgrounds, bold typography, horizontal rules, asymmetric layouts.
 * Light mode is white-dominant; dark mode is true black.
 */
import { Link } from '@inertiajs/react';
import { useHomeContent } from '@/lib/homeContent';
import { ImageCard, VideoCard, ChatDemo, VoiceDemo, HomeQuickActions, HomeImageTrends, HomeVideoTrends } from './HomeSections';

const Rule = () => <div className="w-8 h-px bg-primary inline-block mr-3 align-middle" />;

const EdBadge = ({ text }) => (
    <div className="flex items-center text-primary text-xs font-bold uppercase tracking-[0.3em]">
        <Rule />{text}
    </div>
);

const isVideoUrl = (url) => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url || '');

export default function HomeV3({ page, openRegister }) {
    const c = useHomeContent(page?.content);
    const heroBg = c.hero_bg || c.intro_video;

    return (
        <div className="min-h-screen bg-white dark:bg-[#0a0a0a]">
            {c.custom_css && <style dangerouslySetInnerHTML={{ __html: c.custom_css }} />}

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative min-h-[85vh] flex items-center border-b border-slate-100 dark:border-white/5 overflow-hidden">
                {heroBg && (
                    isVideoUrl(heroBg) ? (
                        <video
                            key={heroBg}
                            className="absolute right-0 top-0 h-full w-full object-cover opacity-30 dark:opacity-25 lg:w-1/2"
                            autoPlay
                            loop
                            muted
                            playsInline
                        >
                            <source src={heroBg} type="video/mp4" />
                        </video>
                    ) : (
                        <img
                            src={heroBg}
                            alt="Hero background"
                            className="absolute right-0 top-0 h-full w-full object-cover opacity-30 dark:opacity-25 lg:w-1/2"
                        />
                    )
                )}
                {/* Gradient mask to keep left side readable */}
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent dark:from-[#0a0a0a] dark:via-[#0a0a0a]/90 dark:to-transparent" />
                <div className="absolute inset-0 dark:hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_40%,rgba(248,250,252,0.24)_100%)]" />
                <div className="max-w-[1200px] mx-auto px-6 py-24 relative z-10">
                    <div className="max-w-3xl">
                        <EdBadge text={c.hero_small_title} />
                        <h1 className="mt-8 mb-10 text-6xl font-black leading-[1.0] tracking-[-0.02em] text-slate-950 dark:text-white md:text-8xl"
                            dangerouslySetInnerHTML={{ __html: c.hero_main_title }} />
                        <p className="mb-14 max-w-xl text-xl leading-relaxed text-slate-600 dark:text-white/50">{c.hero_description}</p>
                        <div className="flex items-center gap-6 flex-wrap">
                            {c.hero_btn_1_url ? (
                                <a href={c.hero_btn_1_url} className="bg-slate-900 dark:bg-white hover:bg-primary text-white dark:text-slate-900 dark:hover:text-black px-12 py-4 font-bold text-base uppercase tracking-wide border-2 border-slate-900 dark:border-white hover:border-primary transition-all">
                                    {c.hero_btn_1_text}
                                </a>
                            ) : (
                                <button onClick={openRegister} className="bg-slate-900 dark:bg-white hover:bg-primary text-white dark:text-slate-900 dark:hover:text-black px-12 py-4 font-bold text-base uppercase tracking-wide border-2 border-slate-900 dark:border-white hover:border-primary transition-all">
                                    {c.hero_btn_1_text}
                                </button>
                            )}
                            <Link href={c.hero_btn_2_url} className="text-slate-900 dark:text-white font-bold text-base flex items-center gap-2 hover:text-primary dark:hover:text-primary transition-colors group">
                                {c.hero_btn_2_text} <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────── */}
            <section className="py-16 border-b border-slate-100 dark:border-white/5">
                <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 dark:divide-white/5">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="px-8 first:pl-0 last:pr-0">
                            <div className="text-5xl font-black text-slate-900 dark:text-white mb-2">{c[`stat_number_${i}`]}</div>
                            <div className="text-slate-400 text-sm uppercase tracking-widest">{c[`stat_label_${i}`]}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Intro ─────────────────────────────────────────── */}
            {c.show_intro !== false && (
                <section className="py-24 bg-slate-50 dark:bg-[#111]">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <EdBadge text={c.intro_badge} />
                            <h2 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white mt-6" dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed" dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            <div className="flex flex-wrap gap-6 pt-2">
                                {[c.intro_check_1, c.intro_check_2].filter(Boolean).map((t, i) => (
                                    <div key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80">
                                        <span className="material-symbols-outlined text-primary text-xl">check_circle</span>
                                        <span className="font-semibold">{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative aspect-video border border-slate-200 dark:border-white/5 overflow-hidden order-1 lg:order-2">
                            <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline>
                                <source src={c.intro_video} type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Quick Actions ────────────────────────────────────────── */}
            {c.show_quick_actions !== false && (
                <section className="py-24 border-b border-slate-100 dark:border-white/5">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-14">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div>
                                <EdBadge text={c.quick_actions_badge || 'AI Models'} />
                                <h2 className="text-4xl font-black text-slate-900 dark:text-white mt-6 leading-tight" dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            </div>
                            <div className="lg:col-span-2 flex items-end">
                                <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed mt-auto">{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                            </div>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* ── Image Trends ──────────────────────────────────────── */}
            {c.show_trends !== false && (
                <section className="py-24 border-b border-slate-100 dark:border-white/5">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-14">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div>
                                <EdBadge text={c.trends_badge || 'Trending'} />
                                <h2 className="text-4xl font-black text-slate-900 dark:text-white mt-6 leading-tight" dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover <span class="text-primary">AI Image Trends</span>' }} />
                            </div>
                            <div className="lg:col-span-2 flex items-end">
                                <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed mt-auto">{c.trends_desc || 'Explore our most popular AI templates and creative presets.'}</p>
                            </div>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* ── Image Studio ──────────────────────────────────── */}
            {c.show_images !== false && (
                <section className="py-24">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-14">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div>
                                <EdBadge text={c.image_studio_badge} />
                                <h2 className="text-4xl font-black text-slate-900 dark:text-white mt-6 leading-tight" dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            </div>
                            <div className="lg:col-span-2">
                                <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed mt-auto">{c.image_studio_desc}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.image_cards.map((item, i) => (
                                <ImageCard key={i} item={item}
                                    cardClass="border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-[#111]"
                                    overlayClass="bg-white/20 dark:bg-black/30" />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Video Trends ──────────────────────────────────── */}
            {c.show_video_trends !== false && (
                <section className="py-24 bg-slate-50 dark:bg-[#111]">
                    <div className="max-w-[1400px] mx-auto px-6 space-y-14">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div>
                                <EdBadge text={c.video_trends_badge || 'Video Trends'} />
                                <h2 className="text-4xl font-black text-slate-900 dark:text-white mt-6 leading-tight" dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending <span class="text-primary">Avatar Templates</span>' }} />
                            </div>
                            <div className="lg:col-span-2">
                                <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed mt-auto">{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                            </div>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* ── Video Studio ──────────────────────────────────── */}
            {c.show_videos !== false && (
                <section className="py-24 bg-slate-50 dark:bg-[#111]">
                    <div className="max-w-[1200px] mx-auto px-6 space-y-14">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div>
                                <EdBadge text={c.video_studio_badge} />
                                <h2 className="text-4xl font-black text-slate-900 dark:text-white mt-6 leading-tight" dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            </div>
                            <div className="lg:col-span-2">
                                <p className="text-slate-500 dark:text-white/50 text-xl leading-relaxed mt-auto">{c.video_studio_desc}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.video_cards.map((item, i) => (
                                <VideoCard key={i} item={item} cardClass="border-slate-200 dark:border-white/5 bg-white dark:bg-[#0a0a0a]" />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Chat ──────────────────────────────────────────── */}
            {c.show_chat !== false && (
                <section className="py-24 border-t border-b border-slate-100 dark:border-white/5">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <EdBadge text={c.chat_badge} />
                            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mt-6" dangerouslySetInnerHTML={{ __html: c.chat_title }} />
                            <p className="text-slate-500 dark:text-white/60 text-xl leading-relaxed">{c.chat_desc}</p>
                            <ul className="space-y-3">
                                {c.chat_features.map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80 border-b border-slate-100 dark:border-white/5 pb-3 group">
                                        <span className="material-symbols-outlined text-primary text-xl group-hover:scale-110 transition-transform">arrow_forward</span>
                                        <span className="font-medium">{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="order-1 lg:order-2">
                            <ChatDemo panelClass="bg-white dark:bg-[#111] border-slate-200 dark:border-white/5" />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Voice ─────────────────────────────────────────── */}
            {c.show_voice !== false && (
                <section className="py-24 bg-slate-50 dark:bg-[#111]">
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <VoiceDemo panelClass="bg-white dark:bg-[#0a0a0a] border-slate-200 dark:border-white/5" />
                        </div>
                        <div className="space-y-8">
                            <EdBadge text={c.voice_badge} />
                            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mt-6" dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                            <p className="text-slate-500 dark:text-white/60 text-xl leading-relaxed">{c.voice_desc}</p>
                            <ul className="space-y-3">
                                {c.voice_features.map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-white/80 border-b border-slate-100 dark:border-white/5 pb-3">
                                        <span className="material-symbols-outlined text-primary text-xl">arrow_forward</span>
                                        <span className="font-medium">{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ───────────────────────────────────────────── */}
            <section className="py-32 border-t border-slate-100 dark:border-white/5">
                <div className="max-w-[1200px] mx-auto px-6 flex flex-col lg:flex-row items-end gap-12 justify-between">
                    <div>
                        <h2 className="text-6xl md:text-7xl font-black text-slate-900 dark:text-white leading-tight mb-4">
                            {c.cta_title}<br /><span className="text-primary">{c.cta_subtitle}</span>
                        </h2>
                        <p className="text-slate-400 dark:text-white/30 text-lg">{c.cta_tagline}</p>
                    </div>
                    <button onClick={openRegister} className="shrink-0 bg-primary hover:bg-primary/90 text-black px-16 py-6 font-black text-xl transition-all hover:shadow-2xl hover:shadow-primary/30 hover:scale-105">
                        {c.cta_btn_primary}
                    </button>
                </div>
            </section>
        </div>
    );
}
